import Highcharts from 'highcharts';
import React, { useEffect } from 'react';
import HighchartsReact from 'highcharts-react-official';
import { ADD_RESPONSE } from '../../../../stores/reducer_add';
import { ADD_HIS_ALERTS } from '../../../../stores/reducer_HistoryData';
import { useAppDispatch, useAppSelector } from '../../../../stores/hooks';
import { useTranslation } from 'react-i18next';

interface TimeIntervalCount {
  sit: number;
  lyingDown: number;
  notInBed: number;
  turnover: number;
  sitUpCountTimesArr: number[];
  lieDownCountTimesArr: number[];
  leaveBedCountTimesArr: number[];
  turnOverCountTimesArr: number[];
}
interface SeriesType {
  name: string;
  data: (number | null)[];
  type?: string;
  color?: string;
  yAxis?: number;
  marker?: {
    enabled: boolean;
    states?: {
      hover: {
        enabled: boolean;
      };
    };
  };
  dataLabels?: {
    enabled: boolean;
  };
  tooltip?: {
    enabled: boolean;
  };
  dashStyle?: string;
  visible?: boolean;
}
interface propsIF {
  timesValuesData: number[];
  observationValuesData: number[];
  regressionData: number[];
  timeIntervalCount: {};
}

const HisAlertColumn = ({ timesValuesData, observationValuesData, timeIntervalCount, regressionData }: propsIF): React.JSX.Element => {
  // ------- redux --------------------
  const dispatch = useAppDispatch();
  const {t} = useTranslation()
  const store_timestamp = useAppSelector((state) => state.store_historyData.timestamp);
  const { sit, lyingDown, notInBed, turnover } = processTimeIntervalCount(timeIntervalCount);

  let startTime = getCurrentTimeInterval({store_timestamp});
  let time_interval = 12;

  const intervalDuration = 2 * 60 * 60 * 1000; // 2 小時，以毫秒表示
  let xAxis = Array.from(Array(time_interval).keys()).map((index) => {
  const time = new Date(startTime + index * intervalDuration);
  const prevDay = new Date(time.getTime() - 24 * 60 * 60 * 1000);
  const timeStr = `${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}`;
  const dateStr = `${String(prevDay.getMonth() + 1).padStart(2, '0')}/${String(prevDay.getDate()).padStart(2, '0')}`;

  // 如果是 00:00 或者是第一個時間點，則包含日期
  if ((timeStr === '00:00' && index !== 0) || (timeStr !== '00:00' && index === 0)) {
    return `${dateStr}<br />${timeStr}`;
  }

    return timeStr;
  });

  const highchart_data_fromat: SeriesType[] = [
    { name: t("HISTORY_RECORD.MOBILITY_SIT_COUNT"), data: sit, color: '#fbbc04' },
    { name: t("HISTORY_RECORD.MOBILITY_LIE"), data: lyingDown, color: '#34a853' },
    { name: t("HISTORY_RECORD.MOBILITY_NIB"), data: notInBed, color: '#4285f4' },
    { name: t("HISTORY_RECORD.MOBILITY_ROLL"), data: turnover, color: '#ffab91' },
  ];

  // if (timesValuesData.some((value) => value !== 0)) {
  //   highchart_data_fromat.push({
  //     name: t("HISTORY_RECORD.MOBILITY_COUNT"),
  //     data: timesValuesData,
  //     type: 'line',
  //     color: '#00acc1',
  //     yAxis: 1,
  //     marker: { enabled: false },
  //     dataLabels: { enabled: false },
  //     tooltip: {
  //       enabled: false,
  //     },
  //   });
  // }
  // if (observationValuesData.some((value) => value !== 0)) {
  //   highchart_data_fromat.push({
  //     name: t("HISTORY_RECORD.MOBILITY_VALUE"),
  //     data: observationValuesData,
  //     type: 'line',
  //     color: '#9575cd',
  //     yAxis: 2,
  //     marker: {
  //       enabled: false,
  //       states: {
  //         hover: {
  //           enabled: false,
  //         },
  //       },
  //     },
  //     tooltip: {
  //       enabled: false,
  //     },
  //     dataLabels: { enabled: false },
  //   });
  //   highchart_data_fromat.push({
  //     name: t("HISTORY_RECORD.MOBILITY_LINE"),
  //     data: regressionData,
  //     type: 'line',
  //     color: '#ff5722',
  //     yAxis: 2,
  //     marker: {
  //       enabled: false,
  //       states: {
  //         hover: {
  //           enabled: false,
  //         },
  //       },
  //     },
  //     tooltip: {
  //       enabled: false,
  //     },
  //     dataLabels: { enabled: false },
  //     dashStyle: 'Dash',
  //     visible: false,
  //   });
  // }
  const chart = {
    chart: { type: 'column', backgroundColor: '#dbfdff', borderRadius: 10 },
    exporting: { enabled: false },
    title: { text: t("HISTORY_RECORD.MOBILITY_DAILY") },
    xAxis: {
      type: 'datetime',
      categories: xAxis,
      //判斷夜間時間 22-08
      plotBands: xAxis.map((timeStr, index) => {
        let hours, minutes;
        if (timeStr.includes('<br />')) {
          const timePart = timeStr.split('<br />')[1];
          [hours, minutes] = timePart.split(':').map(Number);
        } else {
          [hours, minutes] = timeStr.split(':').map(Number);
        }

        const isNightTime = (hours >= 22 || hours < 8);

        if (isNightTime) {
          return {
            color: 'rgba(0, 51, 102, 0.2)',
            from: index - 0.5,
            to: index + 0.5,
            zIndex: 1
          };
        }
        return null;
      }).filter(Boolean)
    },
    yAxis: [
      {
        min: 0,
        startOnTick: false,
        endOnTick: false,
        opposite: true,
        gridLineColor: 'transparent',
        labels: {
          enabled: false,
        },
        title: { text: '' },
      },
      {
        min: 0,
        maxPadding: 0.2,
        startOnTick: false,
        endOnTick: true,
        opposite: false,
        gridLineColor: 'transparent',
        labels: {
          enabled: true,
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
            if (this.value === this.axis.min) {
              return t("HISTORY_RECORD.MOBILITY_SMALL");
            } else if (this.value === this.axis.max) {
              return t("HISTORY_RECORD.MOBILITY_LARGE");
            }
            return '';
          },
        },
        title: { text: t("HISTORY_RECORD.MOBILITY_INTENSITY") },
      },
      {
        min: 0,
        startOnTick: false,
        endOnTick: false,
        opposite: false,
        gridLineColor: 'transparent',
        labels: {
          enabled: false,
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
            return this.value === this.axis.min ? t("HISTORY_RECORD.MOBILITY_SMALL") : t("HISTORY_RECORD.MOBILITY_LARGE");
          },
        },
        title: { text: '' },
      },
    ],
    legend: {
      align: 'right',
      x: -30,
      verticalAlign: 'top',
      y: 20,
      floating: true,
      backgroundColor: Highcharts.defaultOptions.legend?.backgroundColor || 'white',
      borderColor: '#CCC',
      borderWidth: 1,
      shadow: false,
      symbolWidth: 12,
      symbolHeight: 12,
      symbolRadius: 0,
    },
    tooltip: {
      // headerFormat: '<b>{point.x}</b><br/>',
      // pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
      useHTML: true,
      formatter: function (this: Highcharts.TooltipFormatterContextObject): string | false {
        const color = (this.series.options as any).color;
        const stackTotal = (this.point as Highcharts.Point & { stackTotal?: number }).stackTotal;
        if (this.series.type === 'column' && stackTotal !== undefined) {
          return `<span><b>${this.x}</b><br/><span style="color:${color}; font-size: 10px;">■ </span>${this.series.name}: ${this.y}</span>`;
        }
        return false;
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: true,
        },
      },
      line: {
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
        tooltip: {
          enabled: false,
        },
      },
    },
    series: highchart_data_fromat,
  };

  return (
    <>
      <div className="w-100 h-100">
        <HighchartsReact highcharts={Highcharts} options={chart} allowChartUpdate={true} containerProps={{ className: 'w-100 h-100' }} />
      </div>
    </>
  );
};
export default HisAlertColumn;

//整理4項指標資料轉為陣列
function processTimeIntervalCount(data: { [key: string]: TimeIntervalCount }) {
  if (!data) return { sit: [], lyingDown: [], notInBed: [], turnover: [] };
  if (!data?.isOldData && !!data?.newResult) {
    const {sitUpCountTimesArr:sit,lieDownCountTimesArr:lyingDown,leaveBedCountTimesArr:notInBed,turnOverCountTimesArr:turnover} = data.newResult;
    return { sit, lyingDown, notInBed, turnover };
  }
  const sit: (number | null)[] = [];
  const lyingDown: (number | null)[] = [];
  const notInBed: (number | null)[] = [];
  const turnover: (number | null)[] = [];

  (Object.values(data) as TimeIntervalCount[]).forEach((record) => {
    sit.push(record.sit === 0 ? null : record.sit);
    lyingDown.push(record.lyingDown === 0 ? null : record.lyingDown);
    notInBed.push(record.notInBed === 0 ? null : record.notInBed);
    turnover.push(record.turnover === 0 ? null : record.turnover);
  });

  return { sit, lyingDown, notInBed, turnover };
}

  // 根據當前時間決定起始時間
  const getCurrentTimeInterval = ({store_timestamp}:any) => {
    const now = new Date();
    const hours = now.getHours();
    switch (true) {
      case hours >= 0 && hours < 8:
        return new Date(store_timestamp).setHours(0, 0, 0, 0);
      case hours >= 8 && hours < 16:
        return new Date(store_timestamp).setHours(8, 0, 0, 0);
      default:
        return new Date(store_timestamp).setHours(16, 0, 0, 0);
    }
  };
